import { authAttributeSerializer } from "@megaron/access-control";
import { HttpAction } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";
import { uuidSerializer } from "@megaron/uuid";

import { customerCategories } from "./categories";
import { pricingRuleCustomerFieldsSerializer } from "./pricingRules";

export const loyaltyRegistrationStatuses = ["preregistered", "joined", "registered", "deleted"] as const;
export type LoyaltyRegistrationStatus = (typeof loyaltyRegistrationStatuses)[number];

export const customerStatuses = ["vip", "verified", "unverified", "inactive"] as const;
export type CustomerStatus = (typeof customerStatuses)[number];

export const tags = [
  "glazurnik",
  "targi",
  "szkolenie",
  "social_media",
  "hurtownia",
  "polecenia",
  "baza_specjalista",
  "testy",
  "sucha_zabudowa",
];

export const customerAddressSerializer = Serializers.object({
  country: Serializers.string,
  city: Serializers.string,
  street: Serializers.string,
  postalCode: Serializers.string,
  latitude: Serializers.decimal.nullable(),
  longitude: Serializers.decimal.nullable(),
  googlePlaceId: Serializers.string.nullable(),
});

export type CustomerAddress = SerializerValue<typeof customerAddressSerializer>;

export const newCustomerAddress = (input: Partial<CustomerAddress>): CustomerAddress => ({
  country: "",
  city: "",
  street: "",
  postalCode: "",
  googlePlaceId: null,
  latitude: null,
  longitude: null,
  ...input,
});

const customersDetailsSerializer = Serializers.object({
  uuid: uuidSerializer,
  categories: Serializers.stringOneOf(...customerCategories).array(),
  phoneNumber: Serializers.string,
  email: Serializers.email.nullable(),
  address: customerAddressSerializer,
  region: Serializers.string.nullable(),
  name: Serializers.string,
  firstName: Serializers.string,
  lastName: Serializers.string,
  isPro: Serializers.boolean,
  isAffiliate: Serializers.boolean,
  loyaltyRegistrationStatus: Serializers.stringOneOf(...loyaltyRegistrationStatuses).nullable(),
  tags: Serializers.array(Serializers.string),
  status: Serializers.stringOneOf(...customerStatuses),
  lastPurchaseTime: Serializers.datetime.nullable(),
  totalMassKg: Serializers.decimal,
  postSaleDiscount: Serializers.decimal.nullable(),
  isArchived: Serializers.boolean,
  redeemedAffiliateCodeId: Serializers.string.nullable(),
  loyaltyJoinedAt: Serializers.datetime.nullable(),
  alert: Serializers.string.nullable(),
  listedName: Serializers.string.nullable(),
  listedWebsite: Serializers.string.nullable(),
  listedPhone: Serializers.string.nullable(),
  ...pricingRuleCustomerFieldsSerializer.fields,
});

export type CustomerDetails = SerializerValue<typeof customersDetailsSerializer>;

export const customerListObjectSerializer = Serializers.object({
  uuid: uuidSerializer,
  phoneNumber: Serializers.string,
  firstName: Serializers.string.nullable(),
  lastName: Serializers.string.nullable(),
});

export const customerListSerializer = Serializers.object({
  items: Serializers.array(customerListObjectSerializer),
  count: Serializers.integer,
});

export type CustomerListObject = SerializerValue<typeof customerListObjectSerializer>;

export const newCustomerSerializer = Serializers.object({
  uuid: uuidSerializer,
  region: Serializers.string.nullable(),
});

export type NewCustomer = SerializerValue<typeof newCustomerSerializer>;

export const saveCustomerDtoSerializer = Serializers.object({
  name: Serializers.string,
  region: Serializers.string.nullable(),
  phoneNumber: Serializers.string,
  firstName: Serializers.string,
  lastName: Serializers.string,
  email: Serializers.email.nullable(),
  address: customerAddressSerializer,
  status: Serializers.stringOneOf(...customerStatuses),
  tags: Serializers.array(Serializers.string),
  categories: Serializers.stringOneOf(...customerCategories).array(),
  alert: Serializers.string.nullable(),
  listedName: Serializers.string,
  listedWebsite: Serializers.string,
  listedPhone: Serializers.string,
}).partial();

export type SaveCustomerDto = SerializerValue<typeof saveCustomerDtoSerializer>;

export const sortSerializer = Serializers.object({
  field: Serializers.string,
  order: Serializers.stringOneOf("ASC", "DESC"),
});

export type SortFieldKeys = keyof {
  scan: string;
  mass: string;
  interaction: string;
};

export type Sort = SerializerValue<typeof sortSerializer>;

export const tagsSerializer = Serializers.object({
  tags: Serializers.array(Serializers.string),
});

const customerAllowListsSerializer = Serializers.object({
  uuid: uuidSerializer,
  edit: authAttributeSerializer.array(),
  view: authAttributeSerializer.array(),
  viewHistory: authAttributeSerializer.array(),
});
export type CustomerAllowLists = SerializerValue<typeof customerAllowListsSerializer>;

const customerPotentialSerializer = Serializers.object({
  customerId: uuidSerializer,
  productGroup: Serializers.string,
  potential: Serializers.decimal.nullable(),
});
export type CustomerPotential = SerializerValue<typeof customerPotentialSerializer>;

const customerPotentialsQuerySerialzier = Serializers.object({
  groupName: Serializers.string,
  profit: Serializers.decimal,
  potential: Serializers.decimal.nullable(),
});

export const customers = {
  bulkCustomerAttributesQuery: HttpAction({
    path: "/customers/bulk/attributes",
    method: "get",
    qsSerializer: Serializers.object({
      customerUuids: uuidSerializer.array().asJsonString(),
    }).partial(),
    valueSerializer: customerAllowListsSerializer.array(),
    requiresAuth: true as const,
  }),

  getCustomer: HttpAction({
    path: "/customer/:customerId",
    method: "get",
    requiresAuth: true as const,
    valueSerializer: customersDetailsSerializer,
    paramsSerializer: Serializers.object({
      customerId: uuidSerializer,
    }),
    errorSerializer: Serializers.stringOneOf("CustomerNotFound", "Unauthorized"),
  }),

  saveCustomer: HttpAction({
    path: "/customer/:uuid",
    method: "patch",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({
      uuid: uuidSerializer,
    }),
    bodySerializer: saveCustomerDtoSerializer,
    valueSerializer: Serializers.object({ uuid: uuidSerializer }),
    errorSerializer: Serializers.stringOneOf("Unauthorized", "NameNotUnique"),
  }),

  syncLoyaltyCustomers: HttpAction({
    path: "/customers/syncLoyaltyCustomers",
    method: "post",
    requiresAuth: true as const,
  }),

  syncLoyaltyEvents: HttpAction({
    path: "/customers/syncLoyaltyEvents",
    method: "post",
    requiresAuth: true as const,
  }),

  refreshLoyaltyPointsHistory: HttpAction({
    path: "/customers/refreshLoyaltyPointsHistory",
    method: "post",
    requiresAuth: true as const,
  }),

  syncInvitedUsersRelations: HttpAction({
    path: "/customers/syncInvitedUsersRelations",
    method: "post",
    requiresAuth: true as const,
  }),

  checkCustomerAllowLists: HttpAction({
    path: "/customer/:customerId/allowLists",
    method: "get",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({
      customerId: uuidSerializer,
    }),
    errorSerializer: Serializers.stringOneOf("CustomerNotFound"),
    valueSerializer: customerAllowListsSerializer,
  }),

  saveCustomerPotential: HttpAction({
    path: "/customer/potential",
    method: "put",
    requiresAuth: true as const,
    bodySerializer: customerPotentialSerializer,
    valueSerializer: customerPotentialSerializer,
  }),

  customerPotentialsQuery: HttpAction({
    path: "/customer/:customerId/potential",
    method: "get",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({
      customerId: uuidSerializer,
    }),
    valueSerializer: customerPotentialsQuerySerialzier.array(),
  }),

  removeTag: HttpAction({
    path: "/customer/removeTag",
    method: "post",
    requiresAuth: true as const,
    bodySerializer: Serializers.object({ tag: Serializers.string }),
    errorSerializer: Serializers.stringOneOf("Unauthorized", "TagNotFound"),
  }),

  syncCustomersFromGooglePlaces: HttpAction({
    path: "/customers/syncCustomersFromGooglePlaces",
    method: "post",
    requiresAuth: true as const,
    valueSerializer: Serializers.object({
      total: Serializers.integer,
      updatedCount: Serializers.integer,
    }),
  }),

  checkCustomerCredit: HttpAction({
    path: "/customer/:customerId/creditLimit",
    method: "get",
    requiresAuth: true as const,
    paramsSerializer: Serializers.object({
      customerId: uuidSerializer,
    }),
    valueSerializer: Serializers.object({
      limitInclVat: Serializers.decimal,
      limitExclVat: Serializers.decimal,
    }),
  }),
};
